import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ModalOverlay, ModalContainer } from './BaseModal';
import { previewTermRollover, applyTermRolloverGroup } from '../../api/termRolloverApi';
import { gradePromotionPreview, promoteGrades } from '../../api/studentPortalApi';
import { getYearLabel } from '../../api/courseGroupPortalApi';

/**
 * Term End Adjust (2026-09-29): roll every course group of a campus from the previous
 * term into the chosen one. Steps: form -> preview (+ typed confirmation) -> running -> result.
 * Closes only via X / Cancel (project rule: no backdrop close).
 */

const REASON_LABELS = {
  already_new_term: 'Already on the new term',
  not_previous_term: 'Not on the previous term',
  special_term: 'Special term',
  no_term: 'No term set',
  start_before_old_finish: 'New start date is not after this group\'s current finish date',
  year_12: 'Year 12 — handle manually',
  no_year: 'No year level — handle manually'
};

const TITLE_STATUS_LABELS = {
  renamed: '',
  edited: 'edited',
  mismatch: 'Title year does not match year level — check manually',
  no_year_in_title: 'No year in title'
};

const Modal = styled(ModalContainer)`max-width: 960px;`;
const Head = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #e5e7eb;
  h3 { margin: 0; font-size: 18px; }
`;
const Close = styled.button`
  background: none; border: none; font-size: 24px; color: #6b7280; cursor: pointer;
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;
const Body = styled.div`padding: 16px 20px; overflow-y: auto; flex: 1;`;
const Foot = styled.div`
  display: flex; justify-content: flex-end; gap: 8px; padding: 12px 20px; border-top: 1px solid #e5e7eb;
`;
const Field = styled.label`
  display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; font-size: 14px; font-weight: 500;
  select, input { padding: 8px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; }
`;
const Btn = styled.button`
  padding: 8px 16px; border-radius: 6px; border: none; cursor: pointer; font-size: 14px;
  background: ${p => p.$danger ? '#dc2626' : p.$secondary ? '#e5e7eb' : '#F1AF59'};
  color: ${p => p.$secondary ? '#111827' : 'white'};
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const Warn = styled.div`
  background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; border-radius: 8px;
  padding: 12px; margin-bottom: 12px; font-size: 14px;
  ul { margin: 6px 0 0 18px; padding: 0; }
`;
const Info = styled.div`
  background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a8a; border-radius: 8px;
  padding: 12px; margin-bottom: 12px; font-size: 14px;
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 16px;
  th, td { border-bottom: 1px solid #f3f4f6; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f9fafb; }
  input { width: 100%; padding: 4px 6px; border: 1px solid #d1d5db; border-radius: 4px; }
`;
const Section = styled.h4`margin: 16px 0 8px; font-size: 15px;`;
const Muted = styled.span`color: #6b7280; font-size: 12px;`;

const TermEndAdjustModal = ({ campuses, onClose, onDone }) => {
  const [step, setStep] = useState('form'); // form | preview | running | result
  const [campusId, setCampusId] = useState(campuses[0]?.id || '');
  const [newTerm, setNewTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [finishDate, setFinishDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [gradePreview, setGradePreview] = useState(null);
  const [titles, setTitles] = useState({});
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState({});
  const [gradeResult, setGradeResult] = useState(null);
  // "Skip this time": groups the operator holds back from this run. They stay on the old
  // term untouched and come back as 'process' the next time the same rollover is previewed.
  const [excluded, setExcluded] = useState({});

  const args = { campusId, newTerm: Number(newTerm), startDate, finishDate };
  const toProcess = useMemo(() => (preview?.groups || []).filter(g => g.action === 'process'), [preview]);
  const toRun = toProcess.filter(g => !excluded[g.id]);
  const heldBack = toProcess.filter(g => excluded[g.id]);
  const skipped = useMemo(() => (preview?.groups || []).filter(g => g.action !== 'process'), [preview]);
  const expectedConfirm = `Term ${newTerm}`;
  const confirmOk = confirmText.trim().toLowerCase() === expectedConfirm.toLowerCase();
  const failedIds = Object.keys(results).filter(id => results[id].error);

  const formValid = campusId && newTerm && startDate && finishDate && finishDate > startDate;

  const loadPreview = async () => {
    setBusy(true);
    setError('');
    try {
      const body = await previewTermRollover(args);
      setPreview(body);
      setTitles(Object.fromEntries(body.groups.filter(g => g.action === 'process').map(g => [g.id, g.newTitle])));
      setGradePreview(body.isYearRollover ? await gradePromotionPreview(campusId, body.academicYear) : null);
      setExcluded({});
      setConfirmText('');
      setStep('preview');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const runGroups = async (groups) => {
    setStep('running');
    setProgress({ done: 0, total: groups.length });
    const next = { ...results };
    for (let i = 0; i < groups.length; i += 1) {
      const g = groups[i];
      try {
        const body = await applyTermRolloverGroup({
          ...args,
          courseGroupId: g.id,
          newTitle: preview.isYearRollover ? titles[g.id] : undefined
        });
        next[g.id] = body;
      } catch (e) {
        next[g.id] = { error: e.message };
      }
      setResults({ ...next });
      setProgress({ done: i + 1, total: groups.length });
    }
    if (preview.isYearRollover && gradePreview && !gradePreview.alreadyDone && !gradeResult?.promoted) {
      try {
        setGradeResult(await promoteGrades(campusId, preview.academicYear));
      } catch (e) {
        setGradeResult({ error: e.message });
      }
    }
    setStep('result');
    onDone();
  };

  const retryFailed = () => runGroups(toProcess.filter(g => failedIds.includes(g.id)));
  const locked = busy || step === 'running';

  return (
    <ModalOverlay>
      <Modal>
        <Head>
          <h3>Term End Adjust</h3>
          <Close onClick={onClose} disabled={locked}>&times;</Close>
        </Head>
        <Body>
          {error && <Warn>{error}</Warn>}

          {step === 'form' && (
            <>
              {campuses.length > 1 && (
                <Field>Campus
                  <select value={campusId} onChange={e => setCampusId(e.target.value)}>
                    {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
              )}
              <Field>New term
                <select value={newTerm} onChange={e => setNewTerm(e.target.value)}>
                  <option value="">Select...</option>
                  {[1, 2, 3, 4].map(t => <option key={t} value={t}>Term {t}</option>)}
                </select>
              </Field>
              <Field>New term start date
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </Field>
              <Field>New term finish date
                <input type="date" value={finishDate} onChange={e => setFinishDate(e.target.value)} />
              </Field>
              {startDate && finishDate && finishDate <= startDate && (
                <Warn>Finish date must be after start date.</Warn>
              )}
              <Info>
                Only course groups currently on Term {newTerm ? (Number(newTerm) === 1 ? 4 : Number(newTerm) - 1) : '—'} will
                change. Nothing is changed until you confirm on the next screen.
              </Info>
            </>
          )}

          {step === 'preview' && preview && (
            <>
              <Warn>
                <strong>This cannot be undone.</strong> For each of the {toRun.length} course group(s) below that are not ticked &quot;Skip this time&quot;:
                <ul>
                  <li>Credit is settled through the old term&apos;s finish date; absences, end date and discount are cleared; student start date becomes {startDate}.</li>
                  <li>Materials: Week 9/10 are kept (as the old term); every other assignment is deleted.</li>
                  <li>Term becomes Term {newTerm}, {startDate} to {finishDate}; holiday weeks and offset are cleared.</li>
                  {preview.isYearRollover && <li>Year level +1 and the course group name is changed as listed.</li>}
                </ul>
                Assign new-term materials only after this has run.
              </Warn>

              <Section>
                Will be adjusted ({toRun.length}{heldBack.length > 0 && `, ${heldBack.length} skipped this time`})
              </Section>
              <Table>
                <thead>
                  <tr>
                    <th>Skip this time</th>
                    <th>Course group</th>
                    <th>Now</th>
                    {preview.isYearRollover && <th>New name / year</th>}
                    <th>Materials keep / delete</th>
                    <th>Students / credit settled</th>
                  </tr>
                </thead>
                <tbody>
                  {toProcess.map(g => (
                    <tr key={g.id} style={excluded[g.id] ? { opacity: 0.45 } : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          style={{ width: 'auto' }}
                          checked={!!excluded[g.id]}
                          onChange={e => setExcluded({ ...excluded, [g.id]: e.target.checked })}
                        />
                      </td>
                      <td>{g.title}</td>
                      <td>Term {g.term}<br /><Muted>{g.startDate} → {g.finishDate}</Muted></td>
                      {preview.isYearRollover && (
                        <td>
                          <input
                            value={titles[g.id] ?? ''}
                            onChange={e => setTitles({ ...titles, [g.id]: e.target.value })}
                          />
                          <Muted>{getYearLabel(Number(g.year))} → {getYearLabel(Number(g.newYear))} {TITLE_STATUS_LABELS[g.titleStatus]}</Muted>
                        </td>
                      )}
                      <td>{g.materials.keep} / {g.materials.remove}</td>
                      <td>
                        {g.enrollments.count}
                        {g.enrollments.settlements.map(s => (
                          <div key={s.studentId}><Muted>{s.studentName}: {s.previousCredit} → {s.remaining}</Muted></div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {skipped.length > 0 && (
                <>
                  <Section>Not changed ({skipped.length})</Section>
                  <Table>
                    <thead><tr><th>Course group</th><th>Term</th><th>Reason</th></tr></thead>
                    <tbody>
                      {skipped.map(g => (
                        <tr key={g.id}>
                          <td>{g.title}</td>
                          <td>{g.term}</td>
                          <td>{g.action === 'manual' ? <strong>{REASON_LABELS[g.reason]}</strong> : REASON_LABELS[g.reason]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {preview.isYearRollover && gradePreview && (
                <Info>
                  {gradePreview.alreadyDone
                    ? `Student grades were already moved up for ${preview.academicYear} at this campus — that step will be skipped.`
                    : `Student grades will also move up by one for ${preview.academicYear}: ${gradePreview.total} students (including inactive), ${gradePreview.graduating} Year 12 → Graduated (set inactive).`}
                </Info>
              )}

              <Field>Type <strong>{expectedConfirm}</strong> to confirm
                <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder={expectedConfirm} />
              </Field>
            </>
          )}

          {step === 'running' && (
            <Info>Adjusting course groups… {progress.done} / {progress.total}. Do not close this window.</Info>
          )}

          {step === 'result' && (
            <>
              <Info>
                Done: {Object.values(results).filter(r => r.status === 'done').length},
                skipped: {Object.values(results).filter(r => r.status === 'skipped').length},
                failed: {failedIds.length}.
                {gradeResult && !gradeResult.error && ` Student grades: ${gradeResult.promoted} moved up (${gradeResult.graduated} graduated).`}
              </Info>
              {heldBack.length > 0 && (
                <Info>
                  Skipped this time by you (still on Term {Number(newTerm) === 1 ? 4 : Number(newTerm) - 1}, nothing changed):{' '}
                  {heldBack.map(g => g.title).join(', ')}. Run Term End Adjust again with the same term later to adjust them.
                </Info>
              )}
              {gradeResult?.error && <Warn>Student Grade +1 failed: {gradeResult.error}. Run it from Student Management.</Warn>}
              {failedIds.length > 0 && (
                <Warn>
                  Failed:
                  <ul>
                    {toProcess.filter(g => failedIds.includes(g.id)).map(g => (
                      <li key={g.id}>{g.title}: {results[g.id].error}</li>
                    ))}
                  </ul>
                </Warn>
              )}
            </>
          )}
        </Body>
        <Foot>
          {step === 'form' && (
            <>
              <Btn $secondary onClick={onClose} disabled={busy}>Cancel</Btn>
              <Btn onClick={loadPreview} disabled={!formValid || busy}>{busy ? 'Loading...' : 'Next: Review'}</Btn>
            </>
          )}
          {step === 'preview' && (
            <>
              <Btn $secondary onClick={() => setStep('form')}>Back</Btn>
              <Btn $secondary onClick={onClose}>Cancel</Btn>
              <Btn $danger onClick={() => runGroups(toRun)} disabled={!confirmOk || toRun.length === 0}>
                Confirm Term End Adjust ({toRun.length})
              </Btn>
            </>
          )}
          {step === 'result' && (
            <>
              {failedIds.length > 0 && <Btn $danger onClick={retryFailed}>Retry failed</Btn>}
              <Btn $secondary onClick={onClose}>Close</Btn>
            </>
          )}
        </Foot>
      </Modal>
    </ModalOverlay>
  );
};

export default TermEndAdjustModal;
